/**
 * useAudioRecorder Hook
 *
 * Custom React hook for recording audio using the MediaRecorder API.
 * Supports start, pause, resume, and stop functionality.
 *
 * Browser compatibility:
 * - Chrome/Firefox: audio/webm
 * - Safari: audio/mp4
 */

import { useState, useRef, useCallback, useEffect } from 'react'

export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'stopped'

interface UseAudioRecorderReturn {
  status: RecordingStatus
  recordedBlob: Blob | null
  duration: number
  error: string | null
  startRecording: () => Promise<void>
  pauseRecording: () => void
  resumeRecording: () => void
  stopRecording: () => void
  resetRecording: () => void
}

interface UseAudioRecorderOptions {
  onRecordingComplete?: (blob: Blob) => void
  maxDuration?: number // in seconds
  /**
   * Called for each chunk as it is produced by MediaRecorder (≈ every 1s). Used to
   * persist the recording incrementally to a durable store so an in-progress encounter
   * survives a session-timeout logout / reload / crash (CAPA F131). Fire-and-forget;
   * the hook does not await it.
   */
  onChunk?: (chunk: Blob) => void
}

/**
 * Get the best supported MIME type for audio recording
 */
export const getSupportedMimeType = (): string => {
  // Prefer MP4 over WebM — Chrome's WebM/Opus output can produce files
  // that ffmpeg struggles to convert (exit code 183 on EC2 services).
  // MP4/AAC is more universally compatible with server-side processing.
  const types = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/wav'
  ]

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }

  // Fallback - let browser decide
  return ''
}

export const useAudioRecorder = (
  options: UseAudioRecorderOptions = {}
): UseAudioRecorderReturn => {
  const { onRecordingComplete, maxDuration, onChunk } = options

  // Keep the latest onChunk in a ref so the MediaRecorder handler (bound once at
  // start) always calls the current callback without needing to re-bind.
  const onChunkRef = useRef(onChunk)
  onChunkRef.current = onChunk

  const [status, setStatus] = useState<RecordingStatus>('idle')
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
  const [duration, setDuration] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(0)
  const pausedDurationRef = useRef<number>(0)

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    mediaRecorderRef.current = null
  }, [])

  // Start duration timer
  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
    startTimeRef.current = Date.now()

    timerRef.current = setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - startTimeRef.current + pausedDurationRef.current) / 1000
      )
      setDuration(elapsed)

      // Check max duration
      if (maxDuration && elapsed >= maxDuration) {
        stopRecording()
      }
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDuration])

  // Pause duration timer
  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    pausedDurationRef.current += Date.now() - startTimeRef.current
  }, [])

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null)

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      streamRef.current = stream

      // Get supported MIME type
      const mimeType = getSupportedMimeType()
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {}

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, options)
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []
      pausedDurationRef.current = 0

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
          // Persist the chunk durably as it arrives (CAPA F131). Fire-and-forget;
          // failures here must never interrupt recording.
          if (onChunkRef.current) {
            try {
              onChunkRef.current(event.data)
            } catch (_) {
              /* persistence is best-effort; never break the live recording */
            }
          }
        }
      }

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mimeType || 'audio/webm'
        })
        setRecordedBlob(blob)
        setStatus('stopped')

        if (onRecordingComplete) {
          onRecordingComplete(blob)
        }

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())
      }

      // Handle errors
      mediaRecorder.onerror = (event: any) => {
        setError(`Recording error: ${event.error?.message || 'Unknown error'}`)
        setStatus('idle')
        cleanup()
      }

      // Start recording
      mediaRecorder.start(1000) // Collect data every second
      setStatus('recording')
      startTimer()

    } catch (err: any) {
      let errorMessage = 'Failed to start recording'

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.'
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.'
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      setStatus('idle')
    }
  }, [cleanup, onRecordingComplete, startTimer])

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === 'recording') {
      mediaRecorderRef.current.pause()
      setStatus('paused')
      pauseTimer()
    }
  }, [status, pauseTimer])

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === 'paused') {
      mediaRecorderRef.current.resume()
      setStatus('recording')
      startTimer()
    }
  }, [status, startTimer])

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && (status === 'recording' || status === 'paused')) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      mediaRecorderRef.current.stop()
    }
  }, [status])

  // Reset recording
  const resetRecording = useCallback(() => {
    cleanup()
    setRecordedBlob(null)
    setDuration(0)
    setError(null)
    setStatus('idle')
    chunksRef.current = []
    pausedDurationRef.current = 0
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  return {
    status,
    recordedBlob,
    duration,
    error,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording
  }
}

/**
 * Format duration in seconds to MM:SS string
 */
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

export default useAudioRecorder
